import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { LayoutDashboard, Home, Building2, LayoutGrid, MapPin, Settings, ShieldCheck, Languages, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, key: "dashboard", exact: true },
  { to: "/admin/properties", icon: Home, key: "properties" },
  { to: "/admin/apartments", icon: Building2, key: "apartments" },
  { to: "/admin/categories", icon: LayoutGrid, key: "categories" },
  { to: "/admin/pois", icon: MapPin, key: "pois" },
  { to: "/admin/settings", icon: Settings, key: "settings" },
  { to: "/admin/security", icon: ShieldCheck, key: "security" },
  { to: "/admin/translations", icon: Languages, key: "translations" },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const { logout, me } = useAdminAuth();
  const nav = useNavigate();
  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-cream-100 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-cream-50 border-r border-ink-200/40 flex-col">
        <div className="px-5 pt-6 pb-5 flex items-center gap-2">
          <Logo variant="mark" className="w-10 h-10" />
          <div>
            <div className="font-editorial italic text-xl text-ink-900 leading-none">La Piazzetta</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-300 mt-1">Admin</div>
          </div>
        </div>
        <nav className="px-3 pt-3 flex-1">
          {NAV.map(({ to, icon: Icon, key, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={`admin-nav-${key}`}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 text-sm font-medium transition",
                isActive ? "bg-corten-50 text-corten-500" : "text-ink-700 hover:bg-cream-200"
              )}
            >
              <Icon strokeWidth={1.5} className="w-4 h-4" />
              {t(`admin.${key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 pb-5 pt-3 border-t border-ink-200/40">
          <div className="text-[10px] uppercase tracking-[0.16em] text-ink-300 mb-1">Admin</div>
          <div className="text-xs text-ink-700 truncate">{me?.email}</div>
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-corten-500 transition"
          >
            <LogOut className="w-3.5 h-3.5" />
            {t("admin.logout")}
          </button>
        </div>
      </aside>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-cream-50 border-b border-ink-200/40 flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Logo variant="mark" className="w-8 h-8" />
          <span className="font-editorial italic text-lg">Admin</span>
        </div>
        <button onClick={doLogout} className="text-xs text-ink-500"><LogOut className="w-4 h-4" /></button>
      </div>
      {/* Content */}
      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        <div className="md:hidden flex overflow-x-auto hide-scrollbar gap-1 px-3 py-2 border-b border-ink-200/40 bg-cream-50 sticky top-14 z-20">
          {NAV.map(({ to, icon: Icon, key, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={`admin-mnav-${key}`}
              className={({ isActive }) => cn(
                "shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap",
                isActive ? "bg-corten-400 text-white" : "bg-cream-100 text-ink-700"
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(`admin.${key}`)}
            </NavLink>
          ))}
        </div>
        <div className="px-5 md:px-8 py-6 max-w-5xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
